import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { AppState } from "@app/app.reducer";
import { Office } from "@app/models";
import { ExternalTip } from "@app/models/external-tips";
import { ALL_OFFICES } from "@app/settings/external-tips/utils/manage-external-tips-constants";
import * as fromShared from "@app/shared/ngrx/shared.reducer";
import * as fromUser from "@app/shared/user";
import { ROLE_ADMIN } from "@app/shared/utils/roles";
import { EXTERNAL_TIPS_CREATE } from "@app/shared/utils/tab-types";
import { ExternalTipsFormComponent } from "@app/sidebar/external-tips/external-tips-form/external-tips-form.component";
import * as formModes from "@app/sidebar/external-tips/utils/external-tips-form-modes";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import * as _ from "lodash";
import {
  combineLatest as observableCombineLatest,
  debounceTime,
  filter,
  map,
  Observable,
  Subject,
  take,
  takeUntil,
} from "rxjs";
import { SidebarTab } from "../../models/sidebar-tab";
import { ConnectableTab } from "../../sidebar-connectable-tab";
import * as sidebarActions from "../../ngrx/sidebar.actions";
import { closeTab } from "../../ngrx/sidebar.actions";
import * as fromSidebar from "../../ngrx/sidebar.reducer";
import * as externalTipsActions from "../ngrx/external-tips.actions";

interface DataForForm {
  offices: Office[];
  preselectedOfficeId: string;
  canEditOffice: boolean;
}

@Component({
  selector: "create-external-tips",
  templateUrl: "./create-external-tips.component.html",
  styleUrls: [
    "../../sidebar.component.common.scss",
    "./create-external-tips.component.scss",
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateExternalTipsComponent
  implements OnInit, OnDestroy, ConnectableTab
{
  @ViewChild(ExternalTipsFormComponent, { static: false })
  externalTipsFormComponent: ExternalTipsFormComponent;

  proxy$ = new Subject<any>();
  tabType = EXTERNAL_TIPS_CREATE;
  tab$: Observable<SidebarTab>;
  unsubscribe$ = new Subject<void>();
  canEditOffice$: Observable<boolean>;
  userOffice$: Observable<Office>;
  externalTipFormModes = formModes;
  dataForForm$: Observable<DataForForm>;

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.mapStateToProps();
    this.connectTab();
  }

  mapStateToProps(): void {
    this.tab$ = this.store.pipe(select(fromSidebar.getTab(this.tabType)));
    this.userOffice$ = this.store.pipe(select(fromUser.getOffice));
    this.canEditOffice$ = this.store.pipe(select(fromUser.hasRole(ROLE_ADMIN)));
    this.dataForForm$ = observableCombineLatest([
      this.getOffices$(),
      this.getPreselectedOfficeId(),
      this.canEditOffice$,
    ]).pipe(
      map(([offices, preselectedOfficeId, canEditOffice]) => ({
        offices,
        preselectedOfficeId,
        canEditOffice,
      }))
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  connectTab(): void {
    observableCombineLatest([
      this.proxy$.pipe(take(1)),
      this.proxy$.pipe(debounceTime(100)),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([first, current]) => {
        if (_.isEqual(first, current)) {
          this.store.dispatch(
            sidebarActions.resetDirty({ tabType: this.tabType })
          );
        } else {
          this.store.dispatch(
            sidebarActions.markAsDirty({ tabType: this.tabType })
          );
        }
      });
  }

  getOffices$(): Observable<Office[]> {
    return observableCombineLatest([
      this.store.pipe(select(fromShared.getOffices)),
      this.translateService.get("all_offices"),
    ]).pipe(
      map(([offices, allOfficeTranslation]) => ({
        offices,
        allOfficeTranslation,
      })),
      filter((data) => data.offices.length > 0),
      map((data) => {
        const allOfficesItem: Office = {
          name: data.allOfficeTranslation,
          eaOfficeId: ALL_OFFICES,
        };
        return [allOfficesItem].concat(data.offices);
      })
    );
  }

  onSubmit(): void {
    const externalTip: ExternalTip = this.formatExternalTip();

    this.store.dispatch(
      externalTipsActions.createExternalTipRequest({ externalTip })
    );
  }

  formatExternalTip(): ExternalTip {
    return {
      eaOfficeId:
        this.externalTipsFormComponent.externalTipsForm.get("office").value ===
        ALL_OFFICES
          ? "null"
          : this.externalTipsFormComponent.externalTipsForm.get("office").value,
      email: this.externalTipsFormComponent.externalTipsForm.get("email").value,
      name: this.externalTipsFormComponent.externalTipsForm.get("title").value,
    };
  }

  closeTab(): void {
    this.store.dispatch(closeTab({ tabType: this.tabType }));
  }

  private getPreselectedOfficeId(): Observable<string> {
    return observableCombineLatest([
      this.canEditOffice$,
      this.userOffice$,
    ]).pipe(
      map(([isAdmin, userOffice]) => {
        if (isAdmin) {
          return "";
        }
        return userOffice.eaOfficeId;
      })
    );
  }
}
